import type { ComponentProps } from "react";
import { observer } from "mobx-react-lite";
import { format } from "date-fns";
import { et } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ServerTimelineCluster as ServerTimelineClusterType } from "@/domains/documents/utils/timelineClustering";
import type { DocumentRowMapped } from "@/stores/documents/repository";
import { ServerTimelineNode } from "./ServerTimelineNode";

/** Everything a document card needs except where it sits — the timeline owns it. */
export type DocumentNodeProps = Omit<
  ComponentProps<typeof ServerTimelineNode>,
  "side" | "variant" | "pixelPosition"
>;

/** Day heading, in the same date style the cards below it use. */
function formatDayLabel(date: Date, language: string): string {
  const isEstonian = language === "et";
  return format(date, isEstonian ? "d. MMM yyyy" : "MMM d, yyyy", {
    locale: isEstonian ? et : undefined,
  });
}

interface ServerTimelineDayGroupProps {
  /** The day's bucket — supplies the date label and the side of the stem. */
  cluster: ServerTimelineClusterType;
  /** The day's documents, as far as they have been fetched. */
  documents: DocumentRowMapped[];
  /** True while the first page of the day's documents is still loading. */
  isPending: boolean;
  /** True when the day holds more documents than have been fetched. */
  hasMore: boolean;
  /** True while a further page is loading. */
  isLoadingMore: boolean;
  /** Fetch the day's next page of documents. */
  onLoadMore: () => void;
  /** Per-document card props, built by the timeline. */
  nodeProps: (doc: DocumentRowMapped) => DocumentNodeProps;
}

/**
 * ServerTimelineDayGroup — one day of the Days band.
 *
 * Every document gets its own card (a day never collapses into a cluster
 * tile), the day's cards stack tightly on the day's own side of the stem, and
 * the date label sits on the stem above them. Laid out in flow, so the group
 * is as tall as its cards are and consecutive days can be separated by a much
 * wider gap than the documents within one day.
 */
export const ServerTimelineDayGroup = observer(function ServerTimelineDayGroup({
  cluster,
  documents,
  isPending,
  hasMore,
  isLoadingMore,
  onLoadMore,
  nodeProps,
}: ServerTimelineDayGroupProps) {
  const { t, language } = useLanguage();
  const isLeft = cluster.side === "left";
  // The stem-facing edge of the column, so the loading row and the load-more
  // button line up with the cards' connectors rather than the page margin.
  const alignSelf = isLeft ? "self-end" : "self-start";

  return (
    <div className="relative flex">
      {/* Day label, centred on the stem above the day's cards */}
      <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
        {formatDayLabel(cluster.startTime, language)}
      </span>

      <div
        className={cn(
          // gap-1: the tight spacing a cluster's inline document list uses, so
          // a day's documents read as one group.
          "flex w-1/2 flex-col gap-1",
          isLeft ? "pr-2" : "ml-auto pl-2"
        )}
      >
        {documents.map((doc) => (
          <ServerTimelineNode key={doc.id} side={cluster.side} variant="flow" {...nodeProps(doc)} />
        ))}

        {isPending && (
          <div
            className={cn("flex items-center gap-2 py-3 text-sm text-muted-foreground", alignSelf)}
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.timeline.loadingDocuments}
          </div>
        )}

        {/* Days with more documents than one page load the rest in place —
            still one card each, never a cluster tile. */}
        {hasMore &&
          (isLoadingMore ? (
            <div
              className={cn(
                "flex items-center gap-2 py-2 text-xs text-muted-foreground",
                alignSelf
              )}
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.timeline.loadingMore}
            </div>
          ) : (
            <Button variant="ghost" size="sm" className={alignSelf} onClick={onLoadMore}>
              {t.timeline.loadMore}
            </Button>
          ))}
      </div>
    </div>
  );
});
